import * as React from 'react';
import { Autocomplete, Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import useAxios from '../../Hooks/useAxios';
import { DATE_FORMAT } from '../../Constants/Global';

/**
 * A form component for managing process details. It includes fields for selecting an employee,
 * setting an end date, and choosing a process status. The form is designed to be used in a modal or dialog.
 * 
 * @component
 * @example
 * <ProcessForm processData={processData} handleDateChange={handleDateChange} />
 * @param {Object} props - The component props.
 * @param {Object} props.processData - The process data to display in the form.
 * @param {string} props.processData.targetEmployee - The employee associated with the process.
 * @param {string} props.processData.endDate - The end date of the process.
 * @param {number} props.processData.statusId - The current status ID of the process.
 * @param {function} props.handleDateChange - A callback function triggered when the end date is changed.
 * @returns {JSX.Element} The rendered process form component.
 */
function ProcessForm(props) {
  const { processData, handleDateChange } = props;
  const { data, isLoading, error } = useAxios("processStatuses");
  const [statuses, setStatuses] = React.useState([]);

  /**
   * Fetches process statuses from the API and updates the state.
   */
  React.useEffect(() => {
    if (!isLoading) {
      if (error) {
        console.error(error);
      } else {
        setStatuses(data)
      };
    }
  }, [isLoading]);

  /**
   * Renders radio buttons for each process status.
   * 
   * @returns {Array} An array of `FormControlLabel` components representing the process statuses.
   */
  const displayRadioButtonStatus = () => {
    return [...statuses.map((status) =>{
      return <FormControlLabel
          value={status.id}
          control={<Radio />}
          label={status.name}
          disabled
          labelPlacement="bottom"
          sx={{margin: "auto"}}
        />
    })]
  }

  return (
    <>
      <Autocomplete
        disablePortal
        disabled
        options={[]}
        value={processData.targetEmployee}
        sx={{ width: 360 , marginTop: "10px"}}
        renderInput={(params) => <TextField {...params} label="Employee" />}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box pt={"10px"}>
          <DatePicker 
            sx={{ width: 360 , marginTop: "10px"}} 
            disabled={processData.statusId === 3} 
            format={DATE_FORMAT} 
            label="End date" 
            defaultValue={dayjs(processData.endDate, DATE_FORMAT)}
            onChange={handleDateChange} />
        </Box>
      </LocalizationProvider>
      <FormControl sx={{width: "100%"}}>
        <RadioGroup
          row
          aria-labelledby="status-radio-buttons-group-label"
          defaultValue={processData.statusId}
          name="radio-buttons-group"   
        >
          {displayRadioButtonStatus()}
        </RadioGroup>
      </FormControl>
  </>
  );
}

export default ProcessForm;
